import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IonIcon } from "@ionic/react";
import {
  checkmarkCircleOutline,
  chevronDownOutline,
  chevronForwardOutline,
} from "ionicons/icons";

import {
  type ConfigPrice,
  type PlanBadgeKind,
  type RenewalOption,
  type ServiceOption,
  type TermOption,
  classifyPlanPrice,
  filterPricesForRenewal,
  formatMoneyFromCents,
  getHighestTierBadgePriceIds,
  getPriceFeatureNames,
  getSavingsVsRegular,
  groupPlanPricesForDisplay,
} from "./planSelectionPricing";

export interface PlanSelectionFormProps {
  prices: ConfigPrice[];
  selectedPrice: ConfigPrice | null;
  onSelectPrice: (price: ConfigPrice) => void;
  renewalOption: RenewalOption;
  onRenewalOptionChange?: (next: RenewalOption) => void;
  /** Full catalog used to resolve prepaid → recurring twin for savings. Defaults to `prices`. */
  allPricesForSavings?: ConfigPrice[];
  showRenewalToggle?: boolean;
  /** Recurring price id for the user's active plan; that tile is labelled and not selectable. */
  currentPriceId?: string | null;
}

const COLORS = {
  bg: "#F5F7FA",
  text: "#1F2937",
  muted: "#6B7280",
  border: "#E5E7EB",
  success: "#16A34A",
  radio: "#2563EB",
  mostPopular: "#7C3AED",
  bestValue: "#2563EB",
  activateSelected: "#15803D",
  renewSelected: "#1F2937",
  white: "#FFFFFF",
  hoverBg: "#F9FAFB",
};

const YEAR_TERMS: TermOption[] = ["oneYear", "twoYear"];

function cadenceKey(price: ConfigPrice): string {
  const c = classifyPlanPrice(price);
  if (!c) return "choosePlan.priceCadenceYearly";
  if (c.term === "monthly") return "choosePlan.priceCadenceMonthlyShort";
  if (c.term === "twoYear") return "choosePlan.priceCadenceTwoYearsShort";
  return "choosePlan.priceCadenceYearlyShort";
}

const PlanSelectionForm: React.FC<PlanSelectionFormProps> = ({
  prices,
  selectedPrice,
  onSelectPrice,
  renewalOption,
  onRenewalOptionChange,
  allPricesForSavings,
  showRenewalToggle = true,
  currentPriceId = null,
}) => {
  const { t } = useTranslation("onboarding");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const savingsCatalog = allPricesForSavings ?? prices;

  const filteredPrices = useMemo(
    () => filterPricesForRenewal(prices, renewalOption),
    [prices, renewalOption]
  );

  const sections = useMemo(
    () => groupPlanPricesForDisplay(filteredPrices),
    [filteredPrices]
  );

  const badgeById = useMemo(
    () => getHighestTierBadgePriceIds(sections),
    [sections]
  );

  const handleRenewalChange = (next: RenewalOption) => {
    if (next === renewalOption) return;
    onRenewalOptionChange?.(next);
  };

  if (prices.length === 0 || sections.length === 0) {
    return (
      <p style={styles.emptyMessage}>{t("choosePlan.noPlansAvailable")}</p>
    );
  }

  return (
    <div style={styles.root}>
      <style>{`
        .plan-picker-year-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
          width: 100%;
        }
        @media (min-width: 768px) {
          .plan-picker-year-grid {
            grid-template-columns: 1fr 1fr;
          }
        }
        .plan-tile:hover:not(.plan-tile-current) {
          background: ${COLORS.hoverBg};
        }
        .plan-tile-current {
          cursor: not-allowed;
        }
        .plan-tile-expand:hover {
          background: rgba(0, 0, 0, 0.03);
        }
        .plan-tile-select:focus,
        .plan-tile-select:focus-visible {
          outline: none;
        }
      `}</style>

      {showRenewalToggle && onRenewalOptionChange ? (
        <div
          style={styles.toggleWrap}
          role="tablist"
          aria-label={t("choosePlan.renewalToggleLabel")}
        >
          <button
            type="button"
            role="tab"
            aria-selected={renewalOption === "prepay"}
            onClick={() => handleRenewalChange("prepay")}
            style={{
              ...styles.toggleBtn,
              ...(renewalOption === "prepay" ? styles.toggleBtnActivateOn : {}),
            }}
          >
            {renewalOption === "prepay" ? (
              <IonIcon
                icon={checkmarkCircleOutline}
                style={styles.toggleCheck}
              />
            ) : null}
            {t("choosePlan.prepayNowSave")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={renewalOption === "renewLater"}
            onClick={() => handleRenewalChange("renewLater")}
            style={{
              ...styles.toggleBtn,
              ...(renewalOption === "renewLater"
                ? styles.toggleBtnRenewOn
                : {}),
            }}
          >
            {renewalOption === "renewLater" ? (
              <IonIcon
                icon={checkmarkCircleOutline}
                style={styles.toggleCheck}
              />
            ) : null}
            {t("choosePlan.renewLaterToggle")}
          </button>
        </div>
      ) : null}

      {sections.map((section) => (
        <section key={section.accessTier} style={styles.section}>
          <h2 style={styles.sectionTitle}>
            {sectionTitle(section.accessTier, t)}
          </h2>

          <div className="plan-picker-year-grid">
            {YEAR_TERMS.map((term) => {
              const list = section.terms[term];
              if (!list || list.length === 0) return null;
              return (
                <div key={term} style={styles.termColumn}>
                  <h3 style={styles.termHeading}>
                    {termHeading(term, renewalOption, t)}
                  </h3>
                  <div style={styles.tileStack}>
                    {list.map((price) => (
                      <PlanTile
                        key={price.id}
                        price={price}
                        selected={selectedPrice?.id === price.id}
                        expanded={expandedId === price.id}
                        badge={badgeById[price.id]}
                        isCurrentPlan={currentPriceId === price.id}
                        savingsCatalog={savingsCatalog}
                        onSelect={() => {
                          if (currentPriceId === price.id) return;
                          onSelectPrice(price);
                        }}
                        onToggleExpand={() =>
                          setExpandedId((id) =>
                            id === price.id ? null : price.id
                          )
                        }
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {section.terms.monthly && section.terms.monthly.length > 0 ? (
            <div style={styles.monthlyBlock}>
              <h3 style={styles.termHeading}>
                {t("choosePlan.columnMonthly")}
              </h3>
              <div style={styles.tileStack}>
                {section.terms.monthly.map((price) => (
                  <PlanTile
                    key={price.id}
                    price={price}
                    selected={selectedPrice?.id === price.id}
                    expanded={expandedId === price.id}
                    badge={badgeById[price.id]}
                    isCurrentPlan={currentPriceId === price.id}
                    savingsCatalog={savingsCatalog}
                    onSelect={() => {
                      if (currentPriceId === price.id) return;
                      onSelectPrice(price);
                    }}
                    onToggleExpand={() =>
                      setExpandedId((id) => (id === price.id ? null : price.id))
                    }
                  />
                ))}
              </div>
            </div>
          ) : null}
        </section>
      ))}

      <p style={styles.footnote}>{t("choosePlan.bundlingFootnote")}</p>
    </div>
  );
};

function sectionTitle(
  accessTier: ServiceOption,
  t: (key: string) => string
): string {
  return accessTier === "privacyCore"
    ? t("choosePlan.sectionPrivacyCore")
    : t("choosePlan.sectionPrivacyPremium");
}

function termHeading(
  term: TermOption,
  renewal: RenewalOption,
  t: (key: string) => string
): string {
  if (renewal === "prepay") {
    return term === "oneYear"
      ? t("choosePlan.columnOneYearPrepaid")
      : t("choosePlan.columnTwoYearsPrepaid");
  }
  return term === "oneYear"
    ? t("choosePlan.columnOneYear")
    : t("choosePlan.columnTwoYears");
}

function PlanTile({
  price,
  selected,
  expanded,
  badge,
  isCurrentPlan,
  savingsCatalog,
  onSelect,
  onToggleExpand,
}: {
  price: ConfigPrice;
  selected: boolean;
  expanded: boolean;
  badge?: PlanBadgeKind;
  isCurrentPlan: boolean;
  savingsCatalog: ConfigPrice[];
  onSelect: () => void;
  onToggleExpand: () => void;
}) {
  const { t } = useTranslation("onboarding");
  const features = getPriceFeatureNames(price, savingsCatalog);
  const savings = getSavingsVsRegular(price, savingsCatalog);
  const amount = formatMoneyFromCents(price.unit_amount);
  const cadence = t(cadenceKey(price));

  return (
    <div
      className={`plan-tile${isCurrentPlan ? " plan-tile-current" : ""}`}
      style={{
        ...styles.tile,
        ...(isCurrentPlan
          ? styles.tileCurrent
          : selected
          ? styles.tileSelected
          : styles.tileUnselected),
      }}
      aria-disabled={isCurrentPlan}
    >
      {isCurrentPlan ? (
        <span style={{ ...styles.badge, background: COLORS.muted }}>
          {t("choosePlan.currentPlanBadge")}
        </span>
      ) : badge ? (
        <span
          style={{
            ...styles.badge,
            background:
              badge === "mostPopular" ? COLORS.mostPopular : COLORS.bestValue,
          }}
        >
          {badge === "mostPopular"
            ? t("choosePlan.badgeMostPopular")
            : t("choosePlan.badgeBestValue")}
        </span>
      ) : null}

      <div style={styles.tileRow}>
        <div
          className="plan-tile-select"
          style={{
            ...styles.tileSelectArea,
            ...(isCurrentPlan ? styles.tileSelectAreaDisabled : {}),
          }}
          onClick={isCurrentPlan ? undefined : onSelect}
          onKeyDown={
            isCurrentPlan
              ? undefined
              : (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect();
                  }
                }
          }
          role="radio"
          aria-checked={selected || isCurrentPlan}
          aria-disabled={isCurrentPlan}
          tabIndex={isCurrentPlan ? -1 : 0}
        >
          <span
            style={{
              ...styles.radio,
              ...(isCurrentPlan
                ? styles.radioCurrent
                : selected
                ? styles.radioSelected
                : styles.radioUnselected),
            }}
            aria-hidden
          >
            {selected || isCurrentPlan ? (
              <span
                style={{
                  ...styles.radioDot,
                  ...(isCurrentPlan ? styles.radioDotCurrent : {}),
                }}
              />
            ) : null}
          </span>

          <div style={styles.tileMain}>
            <div
              style={{
                ...styles.tileName,
                ...(isCurrentPlan ? styles.tileTextMuted : {}),
              }}
            >
              {price.product?.name ?? t("choosePlan.unnamedPlan")}
            </div>
            {price.product?.description ? (
              <div
                style={{
                  ...styles.tileDesc,
                  ...(isCurrentPlan ? styles.tileTextMuted : {}),
                }}
              >
                {price.product.description}
              </div>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          className="plan-tile-expand"
          style={styles.tileExpandArea}
          aria-expanded={expanded}
          aria-label={
            expanded
              ? t("choosePlan.collapseFeatures")
              : t("choosePlan.expandFeatures")
          }
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
        >
          <div style={styles.tilePricing}>
            <div style={styles.priceLine}>
              <span
                style={{
                  ...styles.priceAmount,
                  ...(isCurrentPlan ? styles.tileTextMuted : {}),
                }}
              >
                {amount}
              </span>
              <span style={styles.priceCadence}>{cadence}</span>
            </div>
            {savings && !isCurrentPlan ? (
              <>
                <div style={styles.saveLine}>
                  {t("choosePlan.saveLabel", {
                    amount: formatMoneyFromCents(savings.saveAmount),
                    percent: savings.savePercent,
                  })}
                </div>
                <div style={styles.regularLine}>
                  {t("choosePlan.regularLabel", {
                    amount: formatMoneyFromCents(savings.regularAmount),
                    cadence,
                  })}
                </div>
              </>
            ) : null}
          </div>

          <span style={styles.chevronBtn} aria-hidden>
            <IonIcon
              icon={expanded ? chevronDownOutline : chevronForwardOutline}
              style={styles.chevronIcon}
            />
          </span>
        </button>
      </div>

      {expanded ? (
        features.length > 0 ? (
          <ul style={styles.featureList}>
            {features.map((featureName, index) => (
              <li key={`${price.id}-f-${index}`} style={styles.featureItem}>
                <IonIcon
                  icon={checkmarkCircleOutline}
                  style={styles.featureIcon}
                  aria-hidden
                />
                <span>{featureName}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p style={styles.noFeatures}>{t("choosePlan.noFeatureDetails")}</p>
        )
      ) : null}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    marginBottom: "16px",
  },
  emptyMessage: {
    width: "100%",
    textAlign: "center",
    fontSize: "14px",
    color: COLORS.muted,
    padding: "20px 0",
  },
  toggleWrap: {
    display: "flex",
    width: "100%",
    maxWidth: "560px",
    margin: "0 auto",
    padding: "4px",
    borderRadius: "999px",
    background: COLORS.bg,
    border: `1px solid ${COLORS.border}`,
    gap: "4px",
  },
  toggleBtn: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    border: "none",
    borderRadius: "999px",
    padding: "12px 16px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    background: "transparent",
    color: COLORS.muted,
    fontFamily: "inherit",
    lineHeight: 1.2,
  },
  toggleBtnActivateOn: {
    background: COLORS.activateSelected,
    color: COLORS.white,
  },
  toggleBtnRenewOn: {
    background: COLORS.renewSelected,
    color: COLORS.white,
  },
  toggleCheck: {
    fontSize: "16px",
  },
  section: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  sectionTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 700,
    color: COLORS.text,
    letterSpacing: "0.02em",
  },
  termColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    minWidth: 0,
  },
  termHeading: {
    margin: 0,
    fontSize: "12px",
    fontWeight: 700,
    color: COLORS.muted,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
  },
  tileStack: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  monthlyBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginTop: "8px",
  },
  tile: {
    position: "relative" as const,
    background: COLORS.white,
    border: `1px solid ${COLORS.border}`,
    borderRadius: "8px",
    padding: "12px 12px 12px 12px",
    transition: "border-color 0.15s, background 0.15s, box-shadow 0.15s",
  },
  tileUnselected: {
    borderColor: COLORS.border,
    boxShadow: "none",
    outline: "none",
  },
  tileCurrent: {
    borderColor: COLORS.border,
    boxShadow: "none",
    outline: "none",
    background: "#F3F4F6",
    opacity: 0.75,
  },
  tileSelected: {
    borderColor: COLORS.radio,
    boxShadow: `0 0 0 1px ${COLORS.radio}`,
    outline: "none",
  },
  tileSelectAreaDisabled: {
    cursor: "not-allowed",
  },
  tileTextMuted: {
    color: COLORS.muted,
  },
  badge: {
    position: "absolute" as const,
    top: "-10px",
    left: "12px",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.04em",
    color: COLORS.white,
    padding: "4px 8px",
    borderRadius: "4px",
    lineHeight: 1,
  },
  tileRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  tileSelectArea: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
    minWidth: 0,
    cursor: "pointer",
    textAlign: "left" as const,
    outline: "none",
    boxShadow: "none",
  },
  tileExpandArea: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
    border: "none",
    background: "transparent",
    padding: "4px 0 4px 8px",
    margin: 0,
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: "inherit",
    color: "inherit",
    outline: "none",
    boxShadow: "none",
  },
  radio: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    border: `2px solid ${COLORS.border}`,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box" as const,
    outline: "none",
    boxShadow: "none",
  },
  radioUnselected: {
    border: `2px solid ${COLORS.border}`,
    borderColor: COLORS.border,
    boxShadow: "none",
    outline: "none",
  },
  radioSelected: {
    border: `2px solid ${COLORS.radio}`,
    borderColor: COLORS.radio,
    boxShadow: "none",
    outline: "none",
  },
  radioCurrent: {
    border: `2px solid ${COLORS.muted}`,
    borderColor: COLORS.muted,
    boxShadow: "none",
    outline: "none",
  },
  radioDot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    background: COLORS.radio,
  },
  radioDotCurrent: {
    background: COLORS.muted,
  },
  tileMain: {
    flex: 1,
    minWidth: 0,
    textAlign: "left" as const,
  },
  tileName: {
    fontSize: "14px",
    fontWeight: 600,
    color: COLORS.text,
    lineHeight: 1.3,
  },
  tileDesc: {
    fontSize: "12px",
    color: COLORS.muted,
    marginTop: "2px",
    lineHeight: 1.35,
  },
  tilePricing: {
    textAlign: "right" as const,
    flexShrink: 0,
  },
  priceLine: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "flex-end",
    gap: "4px",
  },
  priceAmount: {
    fontSize: "16px",
    fontWeight: 700,
    color: COLORS.text,
  },
  priceCadence: {
    fontSize: "12px",
    color: COLORS.muted,
  },
  saveLine: {
    fontSize: "11px",
    fontWeight: 700,
    color: COLORS.success,
    marginTop: "2px",
  },
  regularLine: {
    fontSize: "11px",
    color: COLORS.muted,
    marginTop: "1px",
  },
  chevronBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: COLORS.muted,
    width: "24px",
    height: "24px",
  },
  chevronIcon: {
    width: "18px",
    height: "18px",
    color: COLORS.muted,
  },
  featureList: {
    listStyle: "none",
    margin: "12px 0 0 32px",
    padding: "12px 0 0 0",
    borderTop: `1px solid ${COLORS.border}`,
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  featureItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
    fontSize: "13px",
    color: COLORS.text,
    lineHeight: 1.4,
  },
  featureIcon: {
    color: COLORS.muted,
    flexShrink: 0,
    marginTop: "2px",
    width: "15px",
    height: "15px",
  },
  noFeatures: {
    margin: "12px 0 0 32px",
    padding: "12px 0 0 0",
    borderTop: `1px solid ${COLORS.border}`,
    fontSize: "13px",
    color: COLORS.muted,
  },
  footnote: {
    margin: 0,
    fontSize: "13px",
    color: COLORS.muted,
    lineHeight: 1.5,
  },
};

export default PlanSelectionForm;
